import { localized } from '@weavedev/lit-i18next';
import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { iconChevronLink } from '@/components/icons';
import { baseStyles, rootVariablesStyles } from '@/styles';

@customElement('fg-pagination')
@localized()
export class FgPagination extends LitElement {
  /**
   * The total number of pages.
   */
  @property({ type: Number })
  pages!: number;

  /**
   * The current page.
   */
  @property({ type: Number })
  currentPage = 1;

  /**
   * Used in the {@link calculatePagesInPagination} tot calculate the first page visible in the pagination.
   */
  @state()
  firstPage = 1;

  /**
   * Used in the {@link calculatePagesInPagination} tot calculate the last page visible in the pagination.
   */
  @state()
  lastPage = 5;

  /**
   * An array containing the five page numbers to be displayed in the pagination.
   */
  @state()
  pageNumbers: number[] = [];

  firstUpdated() {
    this.calculatePagesInPagination();
  }

  /**
   * Updates the current page and dispatches a custom event to notify the parent component to do got the the given page.
   *
   * @param {number} page
   */
  goToPage(page: number) {
    this.currentPage = page;

    this.calculatePagesInPagination();

    this.dispatchEvent(
      new CustomEvent('got-to-page', {
        detail: {
          page: page,
        },
      }),
    );
  }

  /**
   * Calculates the pages to be displayed in the pagination. If the total pages is larger than 5, the first and last
   * page will be updated based on the current page.
   */
  calculatePagesInPagination() {
    this.pageNumbers = [];

    if (this.pages < 5) {
      this.lastPage = this.pages;
    }

    if (this.currentPage === this.lastPage && this.currentPage < this.pages) {
      this.lastPage = this.lastPage + 1;
      this.firstPage = this.firstPage + 1;
    }
    if (this.currentPage === this.firstPage && this.currentPage > 1) {
      this.lastPage = this.lastPage - 1;
      this.firstPage = this.firstPage - 1;
    }

    for (let i = this.firstPage; i <= this.lastPage; i++) {
      this.pageNumbers.push(i);
    }
  }

  /**
   * Renders a maximum of 5 page buttons, that are calculated in the {@link calculatePagesInPagination} method.
   *
   * @param page
   * @returns {TemplateResult} A button containing the page number, with a click event to go to the given page.
   */
  renderPageButton(page: number): TemplateResult {
    return html`
      <button
        class="pagination-page ${this.currentPage === page ? 'active' : ''}"
        @click=${() => {
          if (this.currentPage !== page) {
            this.goToPage(page);
          }
        }}
      >
        ${page}
      </button>
      ${page === this.lastPage
        ? nothing
        : html`
            <span class="spacer">|</span>
          `}
    `;
  }

  render() {
    return html`
      ${this.currentPage === 1
        ? nothing
        : html`
            <button
              class="pagination-back"
              @click=${() => this.goToPage(this.currentPage - 1)}
            >
              ${iconChevronLink}
            </button>
          `}
      <div class="pagination-wrapper">
        ${this.pageNumbers.map(page => this.renderPageButton(page))}
      </div>
      ${this.currentPage === this.pages && this.pages < 5
        ? nothing
        : html`
            <button
              class="pagination-next"
              @click=${() => this.goToPage(this.currentPage + 1)}
            >
              ${iconChevronLink}
            </button>
          `}
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    css`
      :host {
        display: flex;
        justify-content: center;
        width: 100%;
      }

      .pagination-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 12px;
      }

      .pagination-page:hover {
        font-weight: 900;
      }

      .pagination-page.active {
        font-weight: 900;
        cursor: default;
      }

      .pagination-back,
      .pagination-next {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        width: 32px;
        height: 32px;
        aspect-ratio: 1 / 1;
        display: grid;
        place-items: center;
      }

      .pagination-back svg,
      .pagination-next svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        width: 8px;
        height: auto;
      }

      .pagination-back svg {
        transform: rotate(180deg);
      }

      .spacer {
        color: var(---fg-gray--800, var(--fg-fallback-gray--800));
        font-size: 0.75rem;
        margin: 0 12px;
      }
    `,
  ];
}
